import { AudioControls } from "./AudioControls";
import { useEffect, useState, type ReactNode } from "react";
import type { AlgorithmOperation } from "@algoquest/schemas";
import { useSessionStore } from "../stores/sessionStore";
import { useSearchAlgorithmStore } from "../stores/searchAlgorithmStore";
import { useSortAlgorithmStore } from "../stores/sortAlgorithmStore";
import type { AlgorithmMode } from "../algorithms/searching/SearchController";
import type { SortAlgorithm } from "../algorithms/sorting/SortController";

export type { AlgorithmMode };
export type { SortAlgorithm };
export type Stage = "searching" | "sorting";

export interface HUDProps {
  stage: Stage;
  mode: AlgorithmMode;
  sortAlgorithm: SortAlgorithm;
  levelLabel: string;
  target: number;
  sortDirection: "ascending" | "descending";
  levelIndex: number;
  levelCount: number;
  onTogglePause: () => void;
  onRestart: () => void;
  onResetCamera: () => void;
  onPrevLevel: () => void;
  onNextLevel: () => void;
  onSwitchStage: (stage: Stage) => void;
  onSwitchMode: (mode: AlgorithmMode) => void;
  onSwitchSortAlgorithm: (algorithm: SortAlgorithm) => void;
  /**
   * Phase 14 — the Workshop 3D preview (`WorkshopPreviewApp.tsx`) reuses
   * this entire component unchanged for its status/stats/trace display
   * rather than forking a second HUD, but has exactly one level loaded
   * and nothing to switch between (no fixture array, no other
   * algorithm to jump to) — showing Phase 01-08's dev-era
   * stage/algorithm toggle buttons and Prev/Next controls there would
   * be actively misleading (they'd imply switching away from the level
   * actually being previewed). Defaults to `true` so every existing
   * caller (just `App.tsx`) is unaffected; only the toggle/Prev/Next
   * row is hidden when `false` — the target/comparisons/status/trace
   * displays below it are unrelated to level-switching and stay exactly
   * as they are either way.
   */
  showLevelSwitcher?: boolean;
  onTutorial?: ()=>void;
  onAccount?:()=>void;
  syncStatus?: string;
  onOpenCampaign?: () => void;
  nextLevelLabel?: string;
  nextLevelDisabled?: boolean;
  learningObjective?: string;
  worldName?: string;
  earnedStars?: number;
  mistakes?: number;
  hints?: number;
  hint?: string;
  onHint?: ()=>void;
  operationBudget?: number;
  intermediate?: boolean;
  resultSummary?: string;
  resultDetails?:ReactNode;
}

function describeSearchOperation(op: AlgorithmOperation): string {
  const index = op.indices?.[0];
  const value = op.values?.[0];
  switch (op.type) {
    case "SCAN":
      return `Scan box #${(index ?? 0) + 1} → ${value}`;
    case "COMPARE":
      return `${value} ≠ target — keep going`;
    case "FOUND":
      return `${value} = target — FOUND at box #${(index ?? 0) + 1}!`;
    case "NOT_FOUND":
      return "NOT_FOUND — search space exhausted";
    case "CHOOSE_LEFT":
      return "Chose LEFT — discarding the upper half";
    case "CHOOSE_RIGHT":
      return "Chose RIGHT — discarding the lower half";
    case "DISCARD_RANGE": {
      const range = op.range;
      return range ? `Eliminated boxes #${range[0] + 1}–#${range[1] + 1}` : "Eliminated a range";
    }
    default:
      return op.type;
  }
}

function describeBubbleSortOperation(op: AlgorithmOperation): string {
  const left = op.indices?.[0];
  const right = op.indices?.[1];
  const leftValue = op.values?.[0];
  const rightValue = op.values?.[1];
  switch (op.type) {
    case "COMPARE":
      return `Compare box #${(left ?? 0) + 1} (${leftValue}) and #${(right ?? 0) + 1} (${rightValue})`;
    case "SWAP":
      return `SWAP → box #${(left ?? 0) + 1}=${leftValue}, #${(right ?? 0) + 1}=${rightValue}`;
    case "KEEP":
      return `KEEP — box #${(left ?? 0) + 1} and #${(right ?? 0) + 1} already in order`;
    case "PASS_COMPLETE":
      return "Pass complete — rightmost value of this pass is now locked in";
    default:
      return op.type;
  }
}

/**
 * Selection Sort's own trace formatter, kept separate from
 * describeBubbleSortOperation (which stays untouched) per the established
 * naming precedent — the two algorithms share operation *field* shapes
 * (indices/values) but mean different things by them (a comparison pair
 * vs. candidate-vs-scan; a confirming pass vs. a placement), so a shared
 * formatter would either read wrong for one algorithm or need the same
 * branching this separate function already provides.
 */
function describeSelectionSortOperation(op: AlgorithmOperation): string {
  const left = op.indices?.[0];
  const right = op.indices?.[1];
  const leftValue = op.values?.[0];
  const rightValue = op.values?.[1];
  switch (op.type) {
    case "COMPARE":
      return `Compare candidate box #${(left ?? 0) + 1} (${leftValue}) vs scan box #${(right ?? 0) + 1} (${rightValue})`;
    case "SELECT_MIN":
      return `New candidate (minimum): box #${(left ?? 0) + 1} = ${leftValue}`;
    case "SELECT_MAX":
      return `New candidate (maximum): box #${(left ?? 0) + 1} = ${leftValue}`;
    case "KEEP":
      return `KEEP — box #${(left ?? 0) + 1} is not a better candidate than box #${(right ?? 0) + 1}`;
    case "PLACE":
      return right !== undefined
        ? `PLACE — swap box #${(left ?? 0) + 1} and #${(right ?? 0) + 1} into position`
        : `PLACE — box #${(left ?? 0) + 1} confirmed already in position`;
    default:
      return op.type;
  }
}

/**
 * Insertion Sort's own trace formatter, kept separate for the same reason
 * describeSelectionSortOperation is — SHIFT/INSERT mean something
 * specific to this algorithm's "carry the key backward through the
 * sorted region" story that neither Bubble nor Selection Sort's
 * formatters would say correctly.
 */
function describeInsertionSortOperation(op: AlgorithmOperation): string {
  const left = op.indices?.[0];
  const right = op.indices?.[1];
  const leftValue = op.values?.[0];
  switch (op.type) {
    case "COMPARE":
      return `Compare sorted box #${(left ?? 0) + 1} (${leftValue}) vs the key currently held at box #${(right ?? 0) + 1}`;
    case "SHIFT":
      return `SHIFT — box #${(left ?? 0) + 1}'s value (${leftValue}) moves into box #${(right ?? 0) + 1}`;
    case "INSERT":
      return `INSERT — key ${leftValue} settles into box #${(left ?? 0) + 1}`;
    default:
      return op.type;
  }
}

/**
 * Phase 05 HUD: added an outer Searching/Sorting stage toggle above the
 * existing Linear/Binary algorithm toggle (which only applies within
 * Searching). Reads from searchAlgorithmStore or sortAlgorithmStore
 * depending on `stage` rather than forcing both domains through one
 * shape, mirroring GameRuntime's own stage split.
 *
 * Phase 06/07 each added a second-level Bubble/Selection/Insertion toggle
 * (mirroring Searching's Linear/Binary toggle) with its own separate
 * per-algorithm store.
 *
 * Phase 08: now that GameRuntime unifies all three sorting algorithms
 * behind SortController, sortAlgorithmStore holds a single SortViewState
 * (`sortView` below) covering all three — the three separate per-algorithm
 * stores from Phase 06/07 are retired. `sortView.swaps`/`sortView.shifts`
 * are BOTH always present on this shared shape (Master Spec §40 treats
 * them as distinct metrics; the inapplicable one is always 0 per
 * algorithm — see SortViewState's own doc comment), which is what
 * resolves the TypeScript union-typing workaround Phase 07 needed here:
 * every stats-bar number now reads straight off `sortView`. Only the
 * per-operation trace text (describe*SortOperation) and the status/hint
 * copy below still branch on `sortAlgorithm`, since those are genuinely
 * different WORDING per algorithm, not different data shapes.
 */
export function HUD({
  stage,
  mode,
  sortAlgorithm,
  levelLabel,
  target,
  sortDirection,
  levelIndex,
  levelCount,
  onTogglePause,
  onRestart,
  onResetCamera,
  onPrevLevel,
  onNextLevel,
  onSwitchStage,
  onSwitchMode,
  onSwitchSortAlgorithm,
  showLevelSwitcher = true,
  onOpenCampaign, nextLevelLabel = "Next level →", nextLevelDisabled = false, learningObjective, worldName,
  earnedStars, mistakes, hints, hint, onHint, operationBudget, onTutorial, onAccount, syncStatus, intermediate=false, resultSummary, resultDetails,
}: HUDProps) {
  const isPaused = useSessionStore((state) => state.isPaused);
  const nearestBoxIndex = useSessionStore((state) => state.nearestBoxIndex);

  const view = useSearchAlgorithmStore((state) => state.view);
  const searchTrace = useSearchAlgorithmStore((state) => state.trace);
  const searchRejectedHintIndex = useSearchAlgorithmStore((state) => state.rejectedHintIndex);
  const choiceRejected = useSearchAlgorithmStore((state) => state.choiceRejected);

  const sortView = useSortAlgorithmStore((state) => state.view);
  const sortTrace = useSortAlgorithmStore((state) => state.trace);
  const sortRejectedHintIndex = useSortAlgorithmStore((state) => state.rejectedHintIndex);
  const decisionRejected = useSortAlgorithmStore((state) => state.decisionRejected);

  const rejectedHintIndex = stage === "sorting" ? sortRejectedHintIndex : searchRejectedHintIndex;
  const trace = stage === "sorting" ? sortTrace : searchTrace;

  const [showRejectedHint, setShowRejectedHint] = useState(false);
  const [showChoiceRejected, setShowChoiceRejected] = useState(false);
  const [showDecisionRejected, setShowDecisionRejected] = useState(false);

  useEffect(() => {
    if (rejectedHintIndex === null) return undefined;
    setShowRejectedHint(true);
    const timeout = window.setTimeout(() => setShowRejectedHint(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [rejectedHintIndex]);

  useEffect(() => {
    if (!choiceRejected) return undefined;
    setShowChoiceRejected(true);
    const timeout = window.setTimeout(() => setShowChoiceRejected(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [choiceRejected]);

  useEffect(() => {
    if (!decisionRejected) return undefined;
    setShowDecisionRejected(true);
    const timeout = window.setTimeout(() => setShowDecisionRejected(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [decisionRejected]);

  const searchStatus = !view
    ? "Loading…"
    : view.foundIndex !== null
      ? `Found at box #${view.foundIndex + 1} after ${view.comparisons} comparison${view.comparisons === 1 ? "" : "s"}`
      : view.isComplete
        ? view.mode === "linear-search"
          ? `Not found after checking all ${view.values.length} boxes`
          : `Not found — range exhausted after ${view.comparisons} comparisons`
        : view.mode === "linear-search"
          ? "Searching… (walk to the highlighted box, press Space)"
          : view.awaitingChoice
            ? "Not a match — choose LEFT (◄/A) or RIGHT (►/D)"
            : "Walk to the highlighted mid box and press Space to inspect it";

  const sortMetricCount = sortAlgorithm === "insertion-sort" ? (sortView?.shifts ?? 0) : (sortView?.swaps ?? 0);
  const sortMetricLabel = sortAlgorithm === "insertion-sort" ? "shift" : "swap";

  const sortAwaitingDecisionCopy =
    sortAlgorithm === "bubble-sort"
      ? "Compared — choose KEEP (▲/W) or SWAP (▼/S)"
      : sortAlgorithm === "selection-sort"
        ? "Compared — choose SKIP (▲/W) or SELECT (▼/S)"
        : "Compared — choose INSERT (▲/W) or SHIFT (▼/S)";

  const sortWalkPromptCopy =
    sortAlgorithm === "bubble-sort"
      ? "Walk to the highlighted pair and press Space to compare"
      : sortAlgorithm === "selection-sort"
        ? "Walk to the highlighted scan box and press Space to compare"
        : "Walk to the highlighted comparison box and press Space to compare";

  const sortStatus = !sortView
    ? "Loading…"
    : sortView.isComplete
      ? `Sorted! ${sortView.passesCompleted} pass${sortView.passesCompleted === 1 ? "" : "es"}, ${sortMetricCount} ${sortMetricLabel}${sortMetricCount === 1 ? "" : "s"}`
      : sortView.awaitingDecision
        ? sortAwaitingDecisionCopy
        : sortWalkPromptCopy;

  const status = stage === "sorting" ? sortStatus : searchStatus;

  const [showTrace, setShowTrace] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const complete = stage === "sorting" ? sortView?.isComplete : view?.isComplete;
  const [showResult,setShowResult]=useState(false);
  useEffect(()=>{
    setShowResult(false);
    if(!complete||isPaused)return;
    const timer=window.setTimeout(()=>setShowResult(true),1600);
    return ()=>window.clearTimeout(timer);
  },[complete,isPaused,levelIndex,stage]);
  const name = (stage === "sorting" ? sortAlgorithm : mode).replaceAll("-", " ");
  const act = (detail:string) => window.dispatchEvent(new CustomEvent("algoquest-action",{detail}));
  const move = (detail:number) => window.dispatchEvent(new CustomEvent("algoquest-move",{detail}));
  const comparison = stage === "sorting" && sortView?.awaitingDecision
    ? sortView.compareIndices.map(index=>sortView.values[index]) : null;
  const lastScan = [...searchTrace].reverse().find(op=>op.type==="SCAN");
  const formatter = stage === "searching" ? describeSearchOperation : sortAlgorithm === "bubble-sort" ? describeBubbleSortOperation : sortAlgorithm === "selection-sort" ? describeSelectionSortOperation : describeInsertionSortOperation;
  return (
    <div className="game-hud">
      <header className="game-top">
        <div className="brand">Algo<span>Quest</span><small>3D</small></div>
        <div className="level-heading"><span>STAGE {stage === "searching" ? "01 · PARCEL DEPOT" : "02 · SORTING WORKSHOP"}</span><strong>{name}</strong><small>{levelLabel}{worldName ? ` · ${worldName}` : ""}</small></div>
        <div className="top-actions"><AudioControls/>{onAccount&&<button onClick={onAccount}>Account</button>}
          {onTutorial && <button onClick={onTutorial}>Learn</button>}
          {onOpenCampaign && <button onClick={onOpenCampaign} title="Return to main menu and pause this level">⌂ Main menu</button>}
          {showLevelSwitcher && <button onClick={()=>setShowLevels(!showLevels)}>☷ Levels</button>}
          <button onClick={onRestart} aria-label="Restart level">↻</button>
          <button onClick={onTogglePause}>{isPaused ? "▶ Resume" : "Ⅱ Pause"}</button>
        </div>
      </header>
      <section className="mission-card">
        <span>{stage === "searching" ? "FIND THE PACKAGE" : `ORDER · ${sortDirection.toUpperCase()}`}</span>
        <strong>{stage === "searching" ? target : comparison ? `${comparison[0]}  :  ${comparison[1]}` : "Compare & arrange"}</strong>
        <small>{stage === "searching" ? "The label is hidden. Scan to discover." : sortAlgorithm === "bubble-sort" ? "Compare neighbours. Decide. Repeat." : sortAlgorithm === "selection-sort" ? "Track the best candidate, then place it." : "Shift the sorted items to make room."}</small>
      </section>
      <aside className="game-stats">{onHint && <span>MISTAKES / HINTS<strong>{mistakes} / {hints}</strong></span>}<span>COMPARISONS<strong>{stage === "searching" ? view?.comparisons ?? 0 : sortView?.comparisons ?? 0}</strong></span>
        {stage === "sorting" && <><span>{sortMetricLabel.toUpperCase()}S<strong>{sortMetricCount}</strong></span><span>PASSES<strong>{sortView?.passesCompleted ?? 0}</strong></span></>}
        {stage === "searching" && view?.range && !view.isComplete && <span>ACTIVE RANGE<strong>{view.range[0]+1}–{view.range[1]+1}</strong></span>}<span>AT PACKAGE<strong>{nearestBoxIndex === null ? "—" : nearestBoxIndex+1}</strong></span>
      </aside>
      {stage === "searching" && lastScan && !complete && <div className="scan-feedback" key={lastScan.sequence}><b>{lastScan.values?.[0]} ≠ {target}</b><span>Not a match. {view?.awaitingChoice ? "Which half can you discard?" : mode === "binary-search" ? "Move to the middle of the remaining range." : "Continue to the next package."}</span></div>}
      <footer className="game-bottom">
        <div className="guide"><span className="guide-icon">◉</span><div><small title={complete ? learningObjective : undefined}>BOT'S FIELD NOTES</small><p>{status.replaceAll("Space", "E / Scan")}</p>
          {hint && <b className="warning">{hint}</b>}
          {showRejectedHint && rejectedHintIndex !== null && <b className="warning">Move to package {rejectedHintIndex+1} first; follow the algorithm's order.</b>}
          {(showChoiceRejected || showDecisionRejected) && <b className="warning">Try again — compare the values and the required order.</b>}
        </div></div>
        <div className="control-bar"><div className="movement"><button disabled={isPaused || !!complete} onClick={()=>move(-1)} aria-label="Move left">←</button><button disabled={isPaused || !!complete} onClick={()=>move(1)} aria-label="Move right">→</button><small>A / D to walk · click a package</small></div>
          <div className="decisions">
            {stage === "searching" && view?.awaitingChoice ? <><button disabled={isPaused} onClick={()=>act("left")}>← Search left</button><button disabled={isPaused} onClick={()=>act("right")}>Search right →</button></> : stage === "sorting" && sortView?.awaitingDecision ? <><button disabled={isPaused} className="primary" onClick={()=>act("advance")}>{sortAlgorithm === "bubble-sort" ? "⇄ Swap" : sortAlgorithm === "selection-sort" ? "Select candidate" : "Shift"}<kbd>S</kbd></button><button disabled={isPaused} onClick={()=>act("resolve")}>{sortAlgorithm === "bubble-sort" ? "Keep" : sortAlgorithm === "selection-sort" ? "Keep candidate" : "Insert"}<kbd>W</kbd></button></> : <button className="primary" disabled={isPaused || !!complete} onClick={()=>act("scan")}>{stage === "searching" ? "⌕ Scan package" : "Compare packages"}<kbd>E</kbd></button>}
          </div>{onHint && <button disabled={isPaused || !!complete} onClick={onHint}>Hint (+1)</button>}<button onClick={()=>setShowTrace(!showTrace)}>≡ {showTrace ? "Hide" : "View"} trace</button><button onClick={onResetCamera} aria-label="Reset camera">⌖</button>
        </div>
      </footer>
      {showTrace && <aside className="trace-panel"><h3>Your algorithm trace</h3><p>Recorded from your actions</p>{trace.length ? <ol>{trace.map(op=><li key={op.sequence}>{formatter(op)}</li>)}</ol> : <p>Scan or compare a package to begin.</p>}</aside>}
      {showLevels && <div className="menu-scrim"><section className="level-menu"><button className="close" onClick={()=>setShowLevels(false)}>✕</button><span className="eyebrow">CHOOSE YOUR ADVENTURE</span><h2>Small steps. Big logic.</h2><div className="stage-cards"><button className={stage==="searching"?"selected":""} onClick={()=>onSwitchStage("searching")}>⌕<strong>Searching</strong><small>Find the hidden package</small></button><button className={stage==="sorting"?"selected":""} onClick={()=>onSwitchStage("sorting")}>▤<strong>Sorting</strong><small>Bring order to the workshop</small></button></div><div className="algorithm-tabs">{stage === "searching" ? (["linear-search","binary-search"] as AlgorithmMode[]).map(m=><button className={mode===m?"selected":""} key={m} onClick={()=>onSwitchMode(m)}>{m.replaceAll("-"," ")}</button>) : (["bubble-sort","selection-sort","insertion-sort"] as SortAlgorithm[]).map(m=><button className={sortAlgorithm===m?"selected":""} key={m} onClick={()=>onSwitchSortAlgorithm(m)}>{m.replaceAll("-"," ")}</button>)}</div><div className="level-navigation"><button onClick={onPrevLevel}>← Previous</button><strong>Level {levelIndex+1} / {levelCount}</strong><button onClick={onNextLevel}>Next →</button></div><button className="primary" onClick={()=>setShowLevels(false)}>Play this level →</button></section></div>}
      {complete && !showResult && !isPaused && <div className="victory-toast" role="status">{stage==="sorting"?"✓ Packages sorted!":view?.foundIndex!==null?"✓ Package found!":"✓ Search complete!"}</div>}
      {complete && showResult && !isPaused && !showLevels && <div className="completion"><div className="stars" aria-label={intermediate||earnedStars===undefined?"Completed":`${earnedStars} stars`}>{intermediate||earnedStars===undefined?"✓":"★".repeat(earnedStars)+"☆".repeat(3-earnedStars)}</div><span>{intermediate?"PHASE COMPLETE":"MISSION COMPLETE"}</span><h2>{stage === "sorting" ? "Perfectly in order!" : view?.foundIndex !== null ? "Package found!" : "Search complete!"}</h2><p>{status}</p>{resultSummary&&<p className="phase-summary">{resultSummary}</p>}{resultDetails}{syncStatus&&<p role="status">{syncStatus}</p>}<p>{onHint?`${mistakes} mistakes · ${hints} hints · 3-star budget: ${operationBudget} operations`:"Every small step in logic builds a smarter you."}</p><div><button onClick={onRestart}>Play again</button>{(showLevelSwitcher || onOpenCampaign) && <button className="primary" disabled={nextLevelDisabled} onClick={onNextLevel}>{nextLevelDisabled ? "Saving…" : nextLevelLabel}</button>}<button onClick={()=>setShowTrace(!showTrace)}>View explanation</button></div></div>}
      {isPaused && !showLevels && <div className="pause-card"><h2>Take a little breather.</h2><button className="primary" onClick={onTogglePause}>Resume adventure</button></div>}
    </div>
  );
}
